import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UtilService } from '../../shared/util.service';
import { DateTime } from 'luxon';
import { SessionService } from '../../_services';
import { DateService } from 'app/shared/services/date.service';
import { Store } from '@ngxs/store';
import { EMPTY } from 'rxjs';
import { CustomersState } from 'app/states/customers/customers.state';
import { CreateCaseDialogService } from 'app/shared/core-lib-modal-services/cases/create-case-dialog.service';
import { auditTime, catchError, distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { InterchainDashboardService } from 'app/inter-chain/dashboard/services/interchain-dashboard.service';
import { RecommendationData } from 'app/inter-chain/dashboard/models/RecommendationData.model';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { HotelService } from 'app/shared/hotel/hotel.service';
import { FollowupPropertiesService } from 'app/inter-chain/followup/properties/followup-properties.service';

interface DashboardCustomer {
  id: number;
  concierge_id?: number | null;
  [key: string]: unknown;
}

@Component({
  selector: 'mh-new-root-dashboard',
  templateUrl: './new-root-dashboard.component.html',
  styleUrls: ['./new-root-dashboard.component.scss'],
  providers: [FollowupPropertiesService],
  standalone: false,
})
export class NewRootDasboardComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private utilService = inject(UtilService);
  private sessionService = inject(SessionService);
  private dateService = inject(DateService);
  private createCaseDialogService = inject(CreateCaseDialogService);
  private route = inject(ActivatedRoute);
  private interchainDashboardService = inject(InterchainDashboardService);
  private store = inject(Store);
  private hotelService = inject(HotelService);

  private customersReady$ = this.store.select(CustomersState.currentCustomer);

  customer: DashboardCustomer | null = null;
  currentLanguage = 'es';
  greeting: string;
  localTime: number = DateTime.now().hour;

  followupEnabled = false;
  onsiteEnabled = false;
  onlineEnabled = false;
  semanticEnabled = false;
  casesEnabled = false;
  conciergeEnabled = false;

  hotelRecommendations: RecommendationData[] = [];
  loadingRecommendations = true;

  private activeCustomerId: number | null = null;

  ngOnInit() {
    this.greeting = this.getGreeting();

    this.route.queryParams
      .pipe(
        filter((params) => !!params?.create_case),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.createCaseDialogService.openModal(this.utilService.currentUser));

    this.customersReady$
      .pipe(
        distinctUntilChanged((previous, current) => previous?.id === current?.id),
        auditTime(200),
        filter((customer) => !!customer),
        tap((customer) => {
          this.activeCustomerId = customer.id;
          this.customer = null;
          this.resetProductFlags();
          this.getRecommendations(customer.id);
        }),
        switchMap((customer) =>
          this.hotelService.getHotel(customer.id).pipe(
            tap((hotel) => {
              const h = hotel as Record<string, unknown>;
              this.followupEnabled = h.followup === true;
              this.onsiteEnabled = h.onsite === true;
              this.onlineEnabled = h.online === true;
              this.semanticEnabled = h.semantic === true;
              this.casesEnabled = h.cases === true;
              this.conciergeEnabled = h.concierge === true;
              this.customer = customer;
            }),
            catchError((err) => {
              console.error('Error loading hotel products', err);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.utilService
      .isLanguageChanged()
      .pipe(
        tap(() => {
          this.refresWidgetUrl();
          if (this.customer) {
            this.getRecommendations(this.customer.id);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.refresWidgetUrl();
  }

  getRecommendations(customerId: number): void {
    this.loadingRecommendations = true;
    const languageId = +this.utilService.getLanguageId() || this.store.selectSnapshot(DefaultLanguageState.languageId);

    this.interchainDashboardService
      .getRecommendations(`${customerId}`, languageId)
      .pipe(
        tap((response) => {
          if (this.activeCustomerId !== customerId) return;
          this.hotelRecommendations = response.data;
        }),
        catchError((err) => {
          if (this.activeCustomerId !== customerId) return EMPTY;
          console.error('Error loading recommendations', err);
          return EMPTY;
        }),
        finalize(() => {
          if (this.activeCustomerId !== customerId) return;
          this.loadingRecommendations = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  refresWidgetUrl() {
    this.currentLanguage = this.utilService.getCurrentLanguage();
  }

  getGreeting() {
    if (this.localTime >= 5 && this.localTime < 12) return 'dashboard.good_morning';
    if (this.localTime >= 12 && this.localTime < 20) return 'dashboard.good_afternoon';
    return 'dashboard.good_night';
  }

  private resetProductFlags(): void {
    this.followupEnabled = false;
    this.onsiteEnabled = false;
    this.onlineEnabled = false;
    this.semanticEnabled = false;
    this.casesEnabled = false;
    this.conciergeEnabled = false;
    this.hotelRecommendations = [];
    this.loadingRecommendations = true;
  }

  get currentUser() {
    return this.sessionService.getCurrentUser();
  }

  get currentDates() {
    return this.dateService.currentDates;
  }
}
